/* Source: /home/sidsolaiyappan/skooch_ai/shared/task_lifecycle.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { getAccessToken } from "./auth_token.js";
import { logInteractionEvent } from "./event_capture.js";
import { maybeAskAfterComplete } from "./learning_handlers.js";
import { updateTaskStateRemote, deleteTaskRemote } from "./task_crud_remote.js";
import { schedulerRequestFailed, schedulerErrorText } from "./scheduler_request_helpers.js";
import { deleteScheduleBlocksForTaskRemote } from "./schedule_block_crud.js";
import { getStoreTasks, getStoreSchedule, getStoreSchedulerTasks, getStoreOverflow, getStoreSchOverflowExpandedIds, getStoreSchPopupBlock, getStoreSchReasoningTarget, getStoreDrawerTaskId, getStoreUserId, getStoreIsAnonymous, setStoreTasks, setStoreSchedule, setStoreSchedulerTasks, setStoreOverflow, setStoreSchOverflowExpandedIds, setStoreSchPopupBlock, setStoreSchReasoningTarget, setStoreSchLog, setStoreSchDirty, setStoreDrawerOpen, setStoreToastResponse, setStoreToastVisible, setStoreToastMCQOptions } from "./store.js";
async function persistTaskState(taskId, status, completed, failurePrefix) {
  let accessToken = await getAccessToken();
  let _r = await updateTaskStateRemote(getStoreUserId(), getStoreIsAnonymous(), accessToken, taskId, status, completed, failurePrefix);
  if (!_r["ok"]) {
    setStoreSchLog(_r["errorMsg"]);
    return false;
  }
  return true;
}
async function clearTaskScheduleBlocks(taskId, failurePrefix, localOnlyNote) {
  let _uid = getStoreUserId();
  if (!_uid) {
    return true;
  }
  let accessToken = await getAccessToken();
  let _res = await deleteScheduleBlocksForTaskRemote(_uid, getStoreIsAnonymous(), accessToken, taskId, failurePrefix, localOnlyNote);
  if (!_res["ok"]) {
    setStoreSchLog(_res["errorMsg"]);
    return false;
  }
  return true;
}
async function deleteTaskEverywhere(taskId, failurePrefix) {
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let savedTasks = getStoreTasks();
  let savedSchedule = getStoreSchedule();
  let savedSchTasks = getStoreSchedulerTasks();
  let savedOverflow = getStoreOverflow();
  let savedOverflowExpanded = getStoreSchOverflowExpandedIds();
  let savedPopup = getStoreSchPopupBlock();
  let savedReasoning = getStoreSchReasoningTarget();
  setStoreTasks(savedTasks.filter(t => {
    return (t.id !== taskId);
  }));
  window.__skoochRemoveTaskFromSchedulerState(taskId);
  if (_uid) {
    let accessToken = await getAccessToken();
    let deletedTask = await deleteTaskRemote(_uid, _isAnon, accessToken, taskId);
    if (schedulerRequestFailed(deletedTask)) {
      setStoreTasks(savedTasks);
      setStoreSchedule(savedSchedule);
      setStoreSchedulerTasks(savedSchTasks);
      setStoreOverflow(savedOverflow);
      setStoreSchOverflowExpandedIds(savedOverflowExpanded);
      setStoreSchPopupBlock(savedPopup);
      setStoreSchReasoningTarget(savedReasoning);
      setStoreSchLog(schedulerErrorText(failurePrefix, deletedTask));
      return false;
    }
  }
  let clearedOk = await clearTaskScheduleBlocks(taskId, "Couldn't remove task from saved schedule", "The task was deleted, but its saved calendar blocks may still appear until you re-optimize.");
  if (!clearedOk) {
    setStoreTasks(savedTasks);
    setStoreSchedule(savedSchedule);
    setStoreSchedulerTasks(savedSchTasks);
    setStoreOverflow(savedOverflow);
    setStoreSchOverflowExpandedIds(savedOverflowExpanded);
    setStoreSchPopupBlock(savedPopup);
    setStoreSchReasoningTarget(savedReasoning);
    return false;
  }
  setStoreSchDirty(true);
  let _hadPlacement = false;
  for (const b of savedSchedule) {
    if ((b.task_id === taskId)) {
      _hadPlacement = true;
    }
  }
  let _delTask = null;
  for (const t of savedTasks) {
    if ((t.id === taskId)) {
      _delTask = t;
    }
  }
  let _dcat = (_delTask ? _delTask.category : null);
  try {
    logInteractionEvent("task_deleted", {"entity_id": taskId, "category": _dcat, "payload": {"had_placement": _hadPlacement}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  if ((getStoreDrawerTaskId() === taskId)) {
    setStoreDrawerOpen(false);
  }
  return true;
}
async function markDone() {
  window.__skoochOpInFlight = true;
  let _drawerTaskId = getStoreDrawerTaskId();
  let savedTasks = getStoreTasks();
  let savedSchedule = getStoreSchedule();
  let savedSchTasks = getStoreSchedulerTasks();
  let savedOverflow = getStoreOverflow();
  let savedOverflowExpanded = getStoreSchOverflowExpandedIds();
  let savedPopup = getStoreSchPopupBlock();
  let savedReasoning = getStoreSchReasoningTarget();
  setStoreTasks(savedTasks.map(t => {
    return ((t.id === _drawerTaskId) ? {"id": t.id, "title": t.title, "category": t.category, "duration_mins": t.duration_mins, "priority": t.priority, "deadline": t.deadline, "status": "done", "completed": true} : t);
  }));
  window.__skoochRemoveTaskFromSchedulerState(_drawerTaskId);
  window.__skoochSyncSchTasksFromPending();
  let stateOk = await persistTaskState(_drawerTaskId, "done", true, "Couldn't save done state");
  if (!stateOk) {
    setStoreTasks(savedTasks);
    setStoreSchedule(savedSchedule);
    setStoreSchedulerTasks(savedSchTasks);
    setStoreOverflow(savedOverflow);
    setStoreSchOverflowExpandedIds(savedOverflowExpanded);
    setStoreSchPopupBlock(savedPopup);
    setStoreSchReasoningTarget(savedReasoning);
    window.__skoochOpInFlight = false;
    return;
  }
  let clearedOk = await clearTaskScheduleBlocks(_drawerTaskId, "Couldn't remove completed task from saved schedule", "The task was marked done, but its saved calendar blocks may still appear until you re-optimize.");
  if (!clearedOk) {
    setStoreTasks(savedTasks);
    setStoreSchedule(savedSchedule);
    setStoreSchedulerTasks(savedSchTasks);
    setStoreOverflow(savedOverflow);
    setStoreSchOverflowExpandedIds(savedOverflowExpanded);
    setStoreSchPopupBlock(savedPopup);
    setStoreSchReasoningTarget(savedReasoning);
    window.__skoochOpInFlight = false;
    return;
  }
  let syncedDevSnapshot = await window.__skoochPersistCurrentDevSnapshot("Completed task removed from dev snapshot.", savedSchTasks, savedSchedule);
  let _doneTask = null;
  for (const t of savedTasks) {
    if ((t.id === _drawerTaskId)) {
      _doneTask = t;
    }
  }
  let _doneBlock = null;
  for (const b of savedSchedule) {
    if ((b.task_id === _drawerTaskId)) {
      _doneBlock = b;
    }
  }
  let _nowIso = Reflect.construct(Date, []).toISOString();
  let _slot = null;
  if ((_doneBlock && _doneBlock.start_time)) {
    let _hm = String(_doneBlock.start_time).split(":");
    if ((_hm.length >= 2)) {
      _slot = ((Number(_hm[0]) * 60) + Number(_hm[1]));
    }
  }
  let _dow = (_doneBlock ? _doneBlock.day : null);
  let _cat = (_doneTask ? _doneTask.category : null);
  let _planned = (_doneTask ? _doneTask.duration_mins : null);
  let _ontime = true;
  if ((_doneTask && _doneTask.deadline)) {
    _ontime = (_nowIso.slice(0, 10) <= String(_doneTask.deadline));
  }
  try {
    logInteractionEvent("task_completed", {"entity_id": _drawerTaskId, "category": _cat, "slot_minutes": _slot, "day_of_week": _dow, "payload": {"scheduled_slot": _slot, "scheduled_day": _dow, "completed_at": _nowIso, "on_time": _ontime, "planned_duration_mins": _planned, "actual_duration_mins": null}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _wasScheduled = (_doneBlock ? true : false);
  let _doneTitle = (_doneTask ? _doneTask.title : "");
  try {
    let _mac = maybeAskAfterComplete(_drawerTaskId, _doneTitle, _cat, _planned, _wasScheduled);
    if ((_mac && _mac.catch)) {
      _mac = _mac.catch(e => {
        return null;
      });
    }
  } catch (__jac_e) {
    if (true) {
      let _emac = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  setStoreDrawerOpen(false);
  window.__skoochOpInFlight = false;
}
async function deleteTask() {
  window.__skoochOpInFlight = true;
  let deletedOk = await deleteTaskEverywhere(getStoreDrawerTaskId(), "Couldn't delete task");
  if (!deletedOk) {
    window.__skoochOpInFlight = false;
    return;
  }
  setStoreDrawerOpen(false);
  window.__skoochOpInFlight = false;
}
async function snoozeTask() {
  let _drawerTaskId = getStoreDrawerTaskId();
  let savedTasks = getStoreTasks();
  let savedSchedule = getStoreSchedule();
  let savedSchTasks = getStoreSchedulerTasks();
  let savedOverflow = getStoreOverflow();
  let savedOverflowExpanded = getStoreSchOverflowExpandedIds();
  let savedPopup = getStoreSchPopupBlock();
  let savedReasoning = getStoreSchReasoningTarget();
  setStoreTasks(savedTasks.map(t => {
    return ((t.id === _drawerTaskId) ? {"id": t.id, "title": t.title, "category": t.category, "duration_mins": t.duration_mins, "priority": t.priority, "deadline": t.deadline, "status": "snoozed", "completed": false} : t);
  }));
  window.__skoochRemoveTaskFromSchedulerState(_drawerTaskId);
  window.__skoochSyncSchTasksFromPending();
  let stateOk = await persistTaskState(_drawerTaskId, "snoozed", false, "Couldn't save snoozed state");
  if (!stateOk) {
    setStoreTasks(savedTasks);
    setStoreSchedule(savedSchedule);
    setStoreSchedulerTasks(savedSchTasks);
    setStoreOverflow(savedOverflow);
    setStoreSchOverflowExpandedIds(savedOverflowExpanded);
    setStoreSchPopupBlock(savedPopup);
    setStoreSchReasoningTarget(savedReasoning);
    return;
  }
  let clearedOk = await clearTaskScheduleBlocks(_drawerTaskId, "Couldn't remove snoozed task from saved schedule", "The task was snoozed, but the saved calendar may still show an older placement until you re-optimize.");
  if (!clearedOk) {
    setStoreTasks(savedTasks);
    setStoreSchedule(savedSchedule);
    setStoreSchedulerTasks(savedSchTasks);
    setStoreOverflow(savedOverflow);
    setStoreSchOverflowExpandedIds(savedOverflowExpanded);
    setStoreSchPopupBlock(savedPopup);
    setStoreSchReasoningTarget(savedReasoning);
    return;
  }
  setStoreToastMCQOptions([]);
  setStoreToastResponse("Snoozed! This task will be rescheduled.");
  setStoreToastVisible(true);
  let _snzBlock = null;
  for (const b of savedSchedule) {
    if ((b.task_id === _drawerTaskId)) {
      _snzBlock = b;
    }
  }
  let _snzTask = null;
  for (const t of savedTasks) {
    if ((t.id === _drawerTaskId)) {
      _snzTask = t;
    }
  }
  let _sfs = null;
  if ((_snzBlock && _snzBlock.start_time)) {
    let _hm = String(_snzBlock.start_time).split(":");
    if ((_hm.length >= 2)) {
      _sfs = ((Number(_hm[0]) * 60) + Number(_hm[1]));
    }
  }
  let _sfd = (_snzBlock ? _snzBlock.day : null);
  let _scat = (_snzTask ? _snzTask.category : null);
  try {
    logInteractionEvent("task_snoozed", {"entity_id": _drawerTaskId, "category": _scat, "slot_minutes": _sfs, "day_of_week": _sfd, "payload": {"from_slot": _sfs, "from_day": _sfd, "defer_to": null}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  setStoreDrawerOpen(false);
}
export {clearTaskScheduleBlocks, deleteTask, deleteTaskEverywhere, markDone, persistTaskState, snoozeTask};
//# sourceMappingURL=task_lifecycle.js.map
